import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Box, Button, TextField, useTheme } from '@mui/material';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CustomDatePicker } from '@ui/components/common/DateTimePicker';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { createTaskAsync, updateTaskAsync, getTasksGroupedByStatusAsync, getTasksByEventIdGroupedByStatusAsync } from '@application/features/task/taskThunk';
import { getAllUsersWithoutPagination } from '@application/features/user/userThunk';
import { getAllVDKEventsForTaskAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { APIStatus } from '@application/enums/api-status';
import SingleSelect from '@ui/components/common/Autocomplete/SingleSelect';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import { PriorityEnum } from '@application/enums/priority-enum';
import { TaskType } from '@application/enums/task-type';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import useAddNewTaskStyles from './useAddNewTaskStyles';
import { convertLocalToBackend } from '@application/utilities/date-helper';
import { useResponsive } from '@ui/style/useResponsive';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getAllPeopleForTasksAsync } from '@application/features/people/peopleThunk';
import { getCollaboratorsByEventAsync } from '@application/features/collaborator/collaboratorThunk';
import { clearEventCollaborators } from '@application/features/collaborator/collaboratorSlice';
import { CollaboratorTaskAccessLevel } from '@application/enums/collaborator-task-access-level';
import { AutomaticTaskType } from '@application/enums/automatic-task-type';
const AddNewTask = ({ dialog, mt = false, onSubmit, onCancel, onClose, height = false, isSubmitting = false, edit = false, taskData, isDrawer = false, formRef, isCreateAllowed = false, padding = false, isActivityTask = false, taskTab = false, ActivityId = null, PersonId = null, onTaskCreated }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const styles = useAddNewTaskStyles(dialog, mt, height);
    const dispatch = useAppDispatch();
    const taskState = useAppSelector((state) => state.task);
    const userState = useAppSelector((state) => state.user);
    const vdkEventState = useAppSelector((state) => state.VDKEvent);
    const peopleState = useAppSelector((state) => state.people);
    const collaboratorState = useAppSelector((state) => state.collaborator);
    const { eventId } = useParams();
    const location = useLocation();
    const currentUserRoles = useSelector((state) => state.auth.currentUser?.role);
    const isCollaborator = Array.isArray(currentUserRoles) && (currentUserRoles.includes('Collaborator') || currentUserRoles.includes('TransportManager'));
    // Helper function to get priority translation key
    const getPriorityTranslationKey = (priorityValue) => {
        switch (priorityValue) {
            case PriorityEnum.High:
                return 'priority.high';
            case PriorityEnum.Medium:
                return 'priority.medium';
            case PriorityEnum.Low:
                return 'priority.low';
            case PriorityEnum.None:
                return 'priority.none';
            default:
                return 'priority.medium';
        }
    };
    const getCollaboratorAccessLevelTranslationKey = (accessLevel) => {
        switch (accessLevel) {
            case CollaboratorTaskAccessLevel.Internal:
                return 'collaboratorAccessLevel.internal';
            case CollaboratorTaskAccessLevel.External:
                return 'collaboratorAccessLevel.external';
            default:
                return 'collaboratorAccessLevel.internal';
        }
    };
    // Function to get priority icon
    const getPriorityIcon = (priority) => {
        switch (priority) {
            case PriorityEnum.High:
                return (_jsx(Box, { width: 12, height: 12, borderRadius: '50%', bgcolor: theme.palette.success.light }));
            case PriorityEnum.Medium:
                return (_jsx(Box, { width: 12, height: 12, borderRadius: '50%', bgcolor: theme.palette.warning.light }));
            case PriorityEnum.Low:
                return _jsx(Box, { width: 12, height: 12, borderRadius: '50%', bgcolor: theme.palette.grey[300] });
            default:
                return _jsx(Box, { width: 12, height: 12, borderRadius: '50%', bgcolor: theme.palette.grey[800] });
        }
    };
    const statusOptions = [
    // { label: 'Active', value: 'active' },
    // { label: 'Inactive', value: 'inactive' },
    // { label: 'Pending', value: 'pending' },
    // { label: 'Archived', value: 4 }
    ];
    const priorityOptions = [
        {
            label: t(getPriorityTranslationKey(PriorityEnum.High)),
            value: PriorityEnum.High,
            icon: getPriorityIcon(PriorityEnum.High)
        },
        {
            label: t(getPriorityTranslationKey(PriorityEnum.Medium)),
            value: PriorityEnum.Medium,
            icon: getPriorityIcon(PriorityEnum.Medium)
        },
        {
            label: t(getPriorityTranslationKey(PriorityEnum.Low)),
            value: PriorityEnum.Low,
            icon: getPriorityIcon(PriorityEnum.Low)
        }
    ];
    const collaboratorAccessLevelOptions = [
        {
            label: t(getCollaboratorAccessLevelTranslationKey(CollaboratorTaskAccessLevel.Internal)),
            value: CollaboratorTaskAccessLevel.Internal,
        },
        {
            label: t(getCollaboratorAccessLevelTranslationKey(CollaboratorTaskAccessLevel.External)),
            value: CollaboratorTaskAccessLevel.External
        }
    ];
    const taskTypeOptions = [
        { label: t('tasks.event'), value: TaskType.Event },
        { label: t('tasks.peopleActivity'), value: TaskType.People_Activity },
        { label: t('tasks.others'), value: TaskType.Others }
    ];
    // Find the default priority option
    const defaultPriorityValue = null;
    const selectedCompanyId = useSelector((state) => state.company.selectedCompanyId);
    useEffect(() => {
        dispatch(getAllUsersWithoutPagination());
        dispatch(getAllPeopleForTasksAsync());
        // Fetch events for dropdown
        dispatch(getAllVDKEventsForTaskAsync());
    }, [selectedCompanyId, dispatch]);
    // Convert users to dropdown options
    const userOptions = userState.usersWithoutPagination.map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id
    }));
    // Convert Collaborator to dropdown options
    const collaboratorOptions = collaboratorState.eventCollaborators?.map((collaborator) => ({
        label: `${collaborator.firstName} ${collaborator.lastName}`,
        value: collaborator.id,
    })) ?? [];
    // Convert events to dropdown options
    const eventOptions = (() => {
        const events = vdkEventState.VDKEventForTasks || [];
        const options = events.map((event) => ({
            label: event.eventName,
            value: event.eventId
        }));
        // If editing a task and the task's event is not in the current company's events,
        // add it to the options so it can be displayed and selected
        if (edit && taskData?.eventId && taskData?.eventName) {
            const currentEventExists = events.some((event) => event.eventId === taskData.eventId);
            if (!currentEventExists) {
                options.unshift({
                    label: taskData.eventName,
                    value: taskData.eventId
                });
            }
        }
        return options;
    })();
    // Convert people to dropdown options with family member relationships
    const peopleOptions = (() => {
        const people = peopleState.peopleForTasks || [];
        const options = [];
        people.forEach((person) => {
            // Add the main person as an option
            options.push({
                label: person.name,
                value: person.id
            });
            // Add each family member as a separate option
            if (person.familyMembers && person.familyMembers.length > 0) {
                person.familyMembers.forEach((family) => {
                    options.push({
                        label: `${family.name} (${family.relation} of ${person.name})`,
                        value: family.id
                    });
                });
            }
        });
        return options;
    })();
    // Create a mapping to track person relationships
    const personRelationshipMap = (() => {
        const people = peopleState.peopleForTasks || [];
        const map = {};
        people.forEach((person) => {
            // Map the main person
            map[person.id] = { isFamilyMember: false, parentPersonId: person.id };
            // Map each family member
            if (person.familyMembers && person.familyMembers.length > 0) {
                person.familyMembers.forEach((family) => {
                    map[family.id] = {
                        isFamilyMember: true,
                        parentPersonId: person.id,
                        relationId: family.id
                    };
                });
            }
        });
        return map;
    })();
    // (moved below after useForm init)
    // Find the assigned users for editing
    const getAssignedUserOption = () => {
        if (!taskData?.assignedTo || taskData.assignedTo.length === 0)
            return [];
        return taskData.assignedTo.map((userId) => {
            const user = userState.usersWithoutPagination.find((u) => u.id === userId);
            return {
                label: user ? `${user.firstName} ${user.lastName}` : 'Unknown User',
                value: userId
            };
        });
    };
    // Find the assigned collaborators for editing
    const getAssignedCollaboratorOptions = (effectiveAccessLevel) => {
        if (!taskData?.collaboratorIds || taskData.collaboratorIds.length === 0)
            return [];
        return taskData.collaboratorIds
            .map((collabId) => {
            const collab = collaboratorState.eventCollaborators?.find((c) => c.id === collabId);
            if (!collab)
                return null;
            // Filtering based on access level
            if (effectiveAccessLevel === CollaboratorTaskAccessLevel.Internal &&
                collab.accessLevel !== CollaboratorTaskAccessLevel.Internal) {
                return null; // Skip non-internal collaborators
            }
            return {
                label: `${collab.firstName} ${collab.lastName}`,
                value: collabId
            };
        })
            .filter((opt) => opt !== null);
    };
    // Find the priority value for editing
    const getPriorityValue = () => {
        const priority = taskData?.priority;
        if (priority === undefined || priority === null || priority === PriorityEnum.None) {
            return defaultPriorityValue;
        }
        return priority;
    };
    // Helper function to get formatted deadline for editing
    const getFormattedDeadline = () => {
        if (!taskData?.deadline)
            return new Date();
        return new Date(taskData.deadline);
    };
    const { control, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm({
        defaultValues: {
            name: edit && taskData ? taskData.name : '',
            assignedTo: edit && taskData ? getAssignedUserOption() : [],
            deadline: edit && taskData ? getFormattedDeadline() : new Date(),
            priority: edit && taskData ? getPriorityValue() : defaultPriorityValue,
            collaborators: edit && taskData ? [] : [],
            collaboratorTaskAccessLevel: edit && taskData && taskData.collaboratorTaskAccessLevel !== null ? taskData.collaboratorTaskAccessLevel : null,
            taskType: null,
            eventId: edit && taskData?.eventId ? taskData.eventId : null,
            peopleId: null,
            relationId: null
        },
        mode: 'onChange'
    });
    const [isEventTask, setisEventTask] = useState(false);
    useEffect(() => {
        if (isActivityTask && ActivityId) {
            setValue('taskType', TaskType.People_Activity);
            setValue('peopleId', PersonId);
        }
        else if (eventId) {
            setValue('taskType', TaskType.Event);
            setValue('eventId', eventId);
            setisEventTask(true);
        }
    }, [isActivityTask, PersonId, eventId, setValue]);
    // Preselect Link To (People) when editing a People_Activity task
    useEffect(() => {
        if (!edit || !taskData)
            return;
        const hasPeople = peopleState.peopleForTasks && (peopleOptions?.length || 0) > 0;
        if (!hasPeople)
            return;
        if (taskData.taskType === TaskType.People_Activity) {
            const preselectId = taskData.relationId ? taskData.relationId : taskData.personId;
            if (preselectId) {
                setValue('peopleId', preselectId);
            }
        }
    }, [edit, taskData, peopleState, peopleOptions, setValue]);
    // Watch taskType to show/hide appropriate Link To fields
    const watchedTaskType = watch('taskType');
    // Update form when events are loaded (for edit mode)
    useEffect(() => {
        // Only set the eventId if events have finished loading and we're in edit mode
        if (edit &&
            taskData?.eventId &&
            vdkEventState.VDKEvents &&
            vdkEventState.VDKEvents.length > 0) {
            const selectedEvent = vdkEventState.VDKEvents.find((event) => event.id === taskData.eventId);
            if (selectedEvent) {
                // Update the form with the selected event ID
                setValue('eventId', selectedEvent.id);
            }
        }
        // Ensure taskType reflects the existing task when editing
        if (edit && taskData?.taskType !== undefined && taskData?.taskType !== null) {
            setValue('taskType', taskData.taskType);
        }
    }, [edit, taskData?.eventId, vdkEventState.VDKEvents, vdkEventState.status, setValue]);
    //Update Collaborator when new Event is selected or access level changes
    const watchedEventId = useWatch({ control, name: "eventId" });
    const watchedAccessLevel = useWatch({ control, name: "collaboratorTaskAccessLevel" });
    useEffect(() => {
        dispatch(clearEventCollaborators());
        const effectiveEventId = eventId || watchedEventId;
        const effectiveAccessLevel = watchedAccessLevel !== null ? watchedAccessLevel : (taskData?.collaboratorTaskAccessLevel ?? null);
        if (effectiveEventId && effectiveAccessLevel !== null) {
            dispatch(getCollaboratorsByEventAsync({ eventId: effectiveEventId, accessLevel: effectiveAccessLevel }))
                .unwrap()
                .then(() => {
                if (edit && taskData?.collaboratorTaskAccessLevel === effectiveAccessLevel) {
                    setValue("collaborators", getAssignedCollaboratorOptions(effectiveAccessLevel));
                }
            })
                .catch((err) => console.error("Failed to fetch collaborators:", err));
        }
    }, [dispatch, eventId, watchedEventId, watchedAccessLevel, edit, taskData?.collaboratorTaskAccessLevel, setValue]);
    const handleFormSubmit = async (data) => {
        try {
            // Validate that priority is selected (should not happen due to form validation, but extra safety)
            if (data.priority === null || data.priority === undefined) {
                return;
            }
            // Validate Link To fields based on task type
            if (edit && taskData) {
                // Update existing task
                const updateData = {
                    id: taskData.id,
                    name: data.name,
                    assignedTo: data.assignedTo.length > 0 ? data.assignedTo.map((option) => option.value) : null,
                    CollaboratorTaskAccessLevel: data.collaboratorTaskAccessLevel != null ? data.collaboratorTaskAccessLevel : taskData.collaboratorTaskAccessLevel,
                    collaboratorIds: data.collaborators.length > 0 ? data.collaborators.map((option) => option.value) : null,
                    deadline: convertLocalToBackend(data.deadline),
                    priority: data.priority,
                    taskType: taskData.taskType,
                    eventId: taskData.taskType === TaskType.Event
                        ? eventId
                            ? String(eventId)
                            : data.eventId
                                ? String(data.eventId)
                                : null
                        : null,
                    completionStatus: taskData.completionStatus,
                    isActivityTask: taskData.isActivityTask,
                    activityId: taskData.activityId,
                    // Adjust people mapping when editing People Activity and link selection changed
                    relationId: taskData.taskType === TaskType.People_Activity
                        ? personRelationshipMap[data.peopleId || '']?.relationId || null
                        : taskData.relationId || null,
                    personId: taskData.taskType === TaskType.People_Activity
                        ? personRelationshipMap[data.peopleId || '']?.parentPersonId ||
                            taskData.personId ||
                            null
                        : taskData.personId || null
                };
                // Call the backend API through Redux thunk
                await dispatch(updateTaskAsync(updateData))
                    .unwrap()
                    .then(() => {
                    if (eventId) {
                        dispatch(getTasksByEventIdGroupedByStatusAsync({ eventId }));
                    }
                    else {
                        dispatch(getTasksGroupedByStatusAsync());
                    }
                    toast.success(t('tasks.taskUpdatedSuccessfully') || 'Task updated successfully!');
                });
                // Close the popup/dialog after successful update
                if (onClose) {
                    onClose();
                }
            }
            else {
                // Create new task
                const createData = {
                    name: data.name,
                    assignedTo: data.assignedTo.length > 0 ? data.assignedTo.map((option) => option.value) : null,
                    deadline: convertLocalToBackend(data.deadline),
                    priority: data.priority,
                    taskType: data.taskType,
                    eventId: null,
                    isActivityTask: isActivityTask,
                    activityId: ActivityId,
                    relationId: null,
                    personId: null
                };
                // Set appropriate fields based on task type
                if (data.taskType === TaskType.Event) {
                    createData.eventId = eventId
                        ? String(eventId)
                        : data.eventId
                            ? String(data.eventId)
                            : null;
                    createData.CollaboratorTaskAccessLevel = data.collaboratorTaskAccessLevel ? data.collaboratorTaskAccessLevel : CollaboratorTaskAccessLevel.Internal;
                    createData.collaboratorIds = data.collaborators.length > 0 ? data.collaborators.map((option) => option.value) : null;
                }
                else if (data.taskType === TaskType.People_Activity && data.peopleId) {
                    const relationship = personRelationshipMap[data.peopleId];
                    if (relationship) {
                        if (relationship.isFamilyMember) {
                            // For family members, set parent person ID and relation ID
                            createData.personId = relationship.parentPersonId;
                            createData.relationId = relationship.relationId;
                        }
                        else {
                            // For main person, set person ID only
                            createData.personId = relationship.parentPersonId;
                        }
                    }
                }
                // For TaskType.Others, all IDs remain null
                // Call the backend API through Redux thunk
                await dispatch(createTaskAsync(createData))
                    .unwrap()
                    .then(() => {
                    if (onTaskCreated) {
                        onTaskCreated(eventId);
                    }
                    else if (eventId) {
                        dispatch(getTasksByEventIdGroupedByStatusAsync({ eventId }));
                    }
                    else {
                        dispatch(getTasksGroupedByStatusAsync());
                    }
                    toast.success(t('tasks.taskCreatedSuccessfully'));
                });
                // Close the popup/dialog after successful creation
                if (onClose) {
                    onClose();
                }
                // Reset form on success - reset to default values including priority
                reset({
                    name: '',
                    assignedTo: [],
                    collaborators: [],
                    collaboratorTaskAccessLevel: null,
                    deadline: new Date(),
                    priority: defaultPriorityValue,
                    taskType: null,
                    eventId: null,
                    peopleId: null,
                    relationId: null
                });
            }
        }
        catch (error) { }
    };
    const isLoading = taskState.status === APIStatus.PENDING || isSubmitting;
    const hasErrors = Object.keys(errors).length > 0;
    const { isMobileIpadNavigation } = useResponsive();
    const isAutomaticTask = edit && taskData &&
        taskData.automaticTaskType !== undefined &&
        taskData.automaticTaskType !== AutomaticTaskType.None;
    let automaticTaskDisplayName = '';
    if (isAutomaticTask && taskData) {
        let params = [];
        if (taskData.actionData) {
            try {
                const parsed = JSON.parse(taskData.actionData);
                if (parsed && Array.isArray(parsed.p))
                    params = parsed.p;
            }
            catch { }
        }
        automaticTaskDisplayName = params.reduce((s, p, i) => s.replace(new RegExp('\\{' + i + '\\}', 'g'), String(p)), t(taskData.name));
    }
    return (_jsx("form", { ref: formRef, onSubmit: handleSubmit(handleFormSubmit), children: _jsx(Box, { sx: {
                position: 'relative',
                paddingLeft: padding ? '8px' : undefined,
                paddingBottom: isMobileIpadNavigation && location.pathname === `/event-tasks/${eventId}`
                    ? '70px'
                    : undefined
            }, children: _jsxs(Box, { sx: {
                    ...styles.main,
                    py: isDrawer || taskTab ? 0 : 1.5
                }, children: [_jsxs(Box, { sx: { ...styles.fieldsContainer }, children: [isAutomaticTask ? (_jsx(Controller, { control: control, name: 'name', render: ({ field }) => (_jsx(TextField, { ...field, value: automaticTaskDisplayName, onChange: () => { }, label: t('tasks.name'), disabled: true, fullWidth: true, variant: 'filled', size: 'small' })) })) : (_jsx(ControlledFilledTextField, { control: control, name: 'name', errorMessage: errors?.name?.message, label: t('tasks.name'), bg: 'grey8', rules: {
                                    required: t('validations.required', { field: t('tasks.name') }),
                                    minLength: {
                                        value: 3,
                                        message: t('validations.minLength', {
                                            field: t('tasks.name'),
                                            min: 3
                                        })
                                    },
                                    maxLength: {
                                        value: 100,
                                        message: t('validations.maxLength', {
                                            field: t('tasks.name'),
                                            max: 100
                                        })
                                    }
                                } })), _jsx(CustomDatePicker, { control: control, name: 'deadline', bg: 'grey8', error: errors?.deadline?.message, label: t('tasks.deadline'), rules: {
                                    required: t('validations.required', { field: t('tasks.deadline') })
                                } }), _jsx(SingleSelect, { control: control, label: t('tasks.taskType'), name: 'taskType', options: taskTypeOptions, error: errors?.taskType?.message, disabled: edit || isActivityTask || isEventTask, bg: 'grey8', rules: {
                                    required: t('validations.required', { field: t('tasks.taskType') })
                                } }), _jsxs(_Fragment, { children: [!eventId && watchedTaskType === TaskType.Event && (_jsx(SingleSelect, { control: control, label: t('tasks.linkTo'), name: 'eventId', options: eventOptions, error: errors?.eventId?.message, bg: 'grey8', rules: {
                                            required: t('validations.required', {
                                                field: t('tasks.linkTo'),
                                            }),
                                            validate: (value) => {
                                                if (value === null || value === undefined || value === '') {
                                                    return t('validations.required', {
                                                        field: t('tasks.linkTo'),
                                                    });
                                                }
                                                return true;
                                            },
                                        } })), watchedTaskType === TaskType.Event && (_jsxs(_Fragment, { children: [_jsx(SingleSelect, { control: control, label: t('tasks.collaboratorAccessLevel'), name: 'collaboratorTaskAccessLevel', options: collaboratorAccessLevelOptions, error: errors?.collaboratorTaskAccessLevel?.message, bg: 'grey8', rules: {
                                                    required: t('validations.required', {
                                                        field: t('tasks.collaboratorAccessLevel'),
                                                    }),
                                                    validate: (value) => {
                                                        if (value === null || value === undefined || value === '') {
                                                            return t('validations.required', {
                                                                field: t('tasks.collaboratorAccessLevel'),
                                                            });
                                                        }
                                                        return true;
                                                    },
                                                } }), _jsx(ControlledHCheckboxAutocomplete, { control: control, label: t('tasks.collaborators'), name: 'collaborators', maxTags: 2, options: collaboratorOptions, bg: 'grey8', showDesignation: false, disabled: isCollaborator, showSelectAll: true, isSortable: true, selectAllLabel: t('actions.selectAll') })] })), watchedTaskType === TaskType.People_Activity && (_jsx(SingleSelect, { control: control, label: t('tasks.linkTo'), name: 'peopleId', bg: 'grey8', options: peopleOptions, error: errors?.peopleId?.message, disabled: isActivityTask, rules: {
                                            required: t('validations.required', {
                                                field: t('tasks.linkTo'),
                                            }),
                                            validate: (value) => {
                                                if (value === null || value === undefined || value === '') {
                                                    return t('validations.required', {
                                                        field: t('tasks.linkTo'),
                                                    });
                                                }
                                                return true;
                                            },
                                        } }))] }), _jsx(ControlledHCheckboxAutocomplete, { control: control, label: t('tasks.assignTo'), name: 'assignedTo', maxTags: 2, options: userOptions, bg: 'grey8', showDesignation: false, disabled: isCollaborator, showSelectAll: true, isSortable: true, selectAllLabel: t('actions.selectAll') }), _jsx(SingleSelect, { control: control, label: t('tasks.priority'), name: 'priority', bg: 'grey8', options: priorityOptions, error: errors?.priority?.message, rules: {
                                    required: t('validations.required', { field: t('tasks.priority') }),
                                    validate: (value) => {
                                        if (value === undefined || value === null || value === PriorityEnum.None) {
                                            return t('validations.required', {
                                                field: t('tasks.priority')
                                            });
                                        }
                                        return true;
                                    }
                                } })] }), !dialog && (_jsx(Button, { type: 'submit', variant: 'contained', size: 'large', color: 'primary', disabled: isLoading || !isCreateAllowed, children: edit ? t('actions.updateTask') : t('actions.addToList') }))] }) }) }));
};
export default AddNewTask;
